/**
 * A company's parked operations, as every screen that touches one reads them.
 *
 * The approval queue itself lives at «تأیید عملیات». The screens an operation
 * is submitted from need two things from here: the toast that says it went to
 * the approver (with a link to the request), and the pending request for one
 * subject, so a wizard can say it is waiting instead of offering the button
 * again.
 */

import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { ROUTES } from '@metal/shared'
import { useToast } from '@metal/ui'
import { ApiError, api, type Paged } from '@metal/ui/api'
import { useSession } from '@metal/ui/session'

export type ApprovalState = 'PENDING' | 'APPROVED' | 'REFUSED' | 'EXECUTED' | 'FAILED' | 'RETURNED'

/** One request of `GET /org/approval-requests`, exactly as the route sends it. */
export interface ApprovalRequest {
  id: string
  operation: string
  operationFa: string
  valueRial: number | null
  valueFa: string | null
  requestedBy: { userId: string; name: string }
  requestedAt: string
  state: ApprovalState
  stateFa: string
  decidedBy?: { userId: string; name: string }
  decidedAt?: string
  reason?: string
  approverRoleId?: string
  approverFa: string
  subjectKey: string | null
  subjectFa: string | null
  subjectHref: string | null
  summary: Array<{ k: string; v: string; wide?: boolean }>
  executedAt?: string
  resultRef: string | null
  resultHref: string | null
  failureFa: string | null
  canDecide: boolean
  isMine: boolean
}

export type ApprovalList = Paged<ApprovalRequest> & { pendingCount: number }

export const approvalKeys = {
  all: ['approvals'] as const,
  list: (query: Record<string, unknown>) => ['approvals', 'list', query] as const,
  one: (id: string) => ['approvals', 'one', id] as const,
  subject: (subject: string) => ['approvals', 'subject', subject] as const,
}

/** The badge tone of each state, the one place it is decided. */
export const APPROVAL_TONE: Record<ApprovalState, 'warning' | 'neutral' | 'success' | 'danger'> = {
  PENDING: 'warning',
  APPROVED: 'neutral',
  EXECUTED: 'success',
  REFUSED: 'danger',
  FAILED: 'danger',
  RETURNED: 'warning',
}

/**
 * The parked answer, on any screen an operation is submitted from: a toast
 * naming the approver, with «مشاهده» opening the request. Returns true when
 * the error was the parked answer, so the caller knows not to say anything
 * else about it.
 */
export function useParkedToast(): (error: unknown) => boolean {
  const toast = useToast()
  const navigate = useNavigate()
  return (error: unknown) => {
    if (!(error instanceof ApiError) || !error.is('APPROVAL_REQUIRED')) return false
    const id = typeof error.meta?.approvalRequestId === 'string' ? error.meta.approvalRequestId : null
    const approverFa = typeof error.meta?.approverFa === 'string' ? error.meta.approverFa : null
    toast.push({
      icon: 'clock',
      message: approverFa ? `برای تأیید ${approverFa} ارسال شد.` : error.message,
      action: id ? { label: 'مشاهده', onClick: () => navigate(ROUTES.approval(id)) } : undefined,
      timeout: 8000,
    })
    return true
  }
}

/**
 * The request waiting on one subject («offer:1057», «offer-cancel:1057»),
 * or null. Only a company parks anything, so a natural person never asks.
 */
export function usePendingApproval(subject: string | null) {
  const { user } = useSession()
  const enabled = Boolean(subject) && user?.partyKind === 'company'
  const query = useQuery({
    queryKey: approvalKeys.subject(subject ?? ''),
    queryFn: () =>
      api.get<ApprovalList>('/org/approval-requests', { subject: subject ?? '', state: 'PENDING', pageSize: 1 }),
    enabled,
    staleTime: 30_000,
  })
  return enabled ? (query.data?.items[0] ?? null) : null
}
